import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, Circle, Clock, Package, XCircle } from 'lucide-react'
import {
  customerCanCancel, formatDateTime, formatMoney, formatNumber, FULFILLMENT_TYPES, ORDER_STATUSES,
  type OrderDetail, type OrderListItem, type OrderStatus,
} from '@najah/core'
import { Badge, Button, Dialog, EmptyState, Spinner, cn, useToast } from '@najah/ui'
import { api, type Paged } from '../lib/api'
import { useAuth } from '../lib/auth'
import { AuthScreen } from './AuthScreen'

const STATUS_TONE: Record<OrderStatus, 'success' | 'warning' | 'danger' | 'info' | 'neutral'> = {
  PENDING: 'warning',
  CONFIRMED: 'info',
  PREPARING: 'info',
  READY: 'primary' as never,
  OUT_FOR_DELIVERY: 'info',
  DELIVERED: 'success',
  CANCELLED: 'neutral',
  REJECTED: 'danger',
}

/** المسار المتوقّع للطلب — يُرسَم كخطوات ليعرف الزبون أين وصل */
const TRACK_STEPS: OrderStatus[] = ['PENDING', 'CONFIRMED', 'PREPARING', 'READY', 'DELIVERED']

function OrderTimeline({ order }: { order: OrderDetail }) {
  const reached = new Set(order.history.map((h) => h.status))
  const isCancelled = order.status === 'CANCELLED' || order.status === 'REJECTED'

  if (isCancelled) {
    return (
      <div className="flex items-center gap-3 rounded-xl bg-danger-subtle p-4">
        <XCircle className="size-6 shrink-0 text-danger" />
        <div>
          <p className="font-medium text-danger">{ORDER_STATUSES[order.status]}</p>
          {order.staffNote && <p className="mt-0.5 text-sm text-fg-muted">{order.staffNote}</p>}
        </div>
      </div>
    )
  }

  const steps = order.fulfillment === 'DELIVERY'
    ? ['PENDING', 'CONFIRMED', 'PREPARING', 'READY', 'OUT_FOR_DELIVERY', 'DELIVERED'] as OrderStatus[]
    : TRACK_STEPS

  return (
    <ol className="space-y-0">
      {steps.map((step, index) => {
        const done = reached.has(step)
        const current = order.status === step
        const entry = order.history.find((h) => h.status === step)

        return (
          <li key={step} className="flex gap-3">
            <div className="flex flex-col items-center">
              {done ? (
                <CheckCircle2 className={cn('size-5 shrink-0', current ? 'text-primary' : 'text-success')} />
              ) : (
                <Circle className="size-5 shrink-0 text-border-strong" />
              )}
              {index < steps.length - 1 && (
                <span className={cn('my-0.5 w-px flex-1', done ? 'bg-success' : 'bg-border')} />
              )}
            </div>

            <div className={cn('pb-5', index === steps.length - 1 && 'pb-0')}>
              <p className={cn('text-sm font-medium', done ? 'text-fg' : 'text-fg-subtle')}>
                {ORDER_STATUSES[step]}
              </p>
              {entry && <p className="text-xs text-fg-subtle">{formatDateTime(entry.at)}</p>}
            </div>
          </li>
        )
      })}
    </ol>
  )
}

export function OrdersScreen({ notificationOrderId }: { notificationOrderId?: string | null }) {
  const { customer } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [openId, setOpenId] = useState<string | null>(null)
  useEffect(() => { if (notificationOrderId) setOpenId(notificationOrderId) }, [notificationOrderId])

  const { data, isPending } = useQuery({
    queryKey: ['my-orders', customer?.id],
    queryFn: () => api<Paged<OrderListItem>>('/orders'),
    enabled: !!customer,
  })

  const { data: detail } = useQuery({
    queryKey: ['my-orders', customer?.id, openId],
    queryFn: () => api<OrderDetail>(`/orders/${openId}`),
    enabled: !!customer && !!openId,
  })

  const cancelMutation = useMutation({
    mutationFn: (id: string) => api(`/orders/${id}/cancel`, { method: 'POST', body: { reason: 'ألغاه الزبون' } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['my-orders'] })
      toast.success('أُلغي الطلب')
      setOpenId(null)
    },
    onError: (err) => toast.error('تعذّر الإلغاء', err instanceof Error ? err.message : undefined),
  })

  if (!customer) return <AuthScreen />

  if (isPending) {
    return <div className="grid place-items-center py-20"><Spinner className="size-7 text-primary" /></div>
  }

  if (!data || data.items.length === 0) {
    return (
      <EmptyState
        icon={<Package className="size-12" />}
        title="لا توجد طلبات بعد"
        description="طلباتك من المكتبة ستظهر هنا"
      />
    )
  }

  return (
    <div className="space-y-3 p-4">
      {data.items.map((order) => (
        <button
          key={order.id}
          type="button"
          onClick={() => setOpenId(order.id)}
          className="w-full rounded-xl border border-border bg-surface p-4 text-start"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="font-mono text-xs text-fg-muted" dir="ltr">{order.reference}</p>
              <p className="mt-1 text-sm text-fg-subtle">
                <Clock className="me-1 inline size-3" />
                {formatDateTime(order.placedAt)}
              </p>
            </div>
            <Badge tone={STATUS_TONE[order.status]} dot>{ORDER_STATUSES[order.status]}</Badge>
          </div>

          <div className="mt-3 flex items-center justify-between border-t border-border pt-3">
            <span className="text-xs text-fg-muted">
              {formatNumber(order.itemCount)} صنف · {FULFILLMENT_TYPES[order.fulfillment]}
            </span>
            <span className="font-bold text-fg" dir="ltr">{formatMoney(order.total)}</span>
          </div>
        </button>
      ))}

      <Dialog
        open={!!openId}
        onClose={() => setOpenId(null)}
        size="md"
        title={detail?.reference}
        description={detail ? formatDateTime(detail.placedAt) : undefined}
        footer={
          detail && customerCanCancel(detail.status) ? (
            <Button
              variant="danger"
              onClick={() => cancelMutation.mutate(detail.id)}
              loading={cancelMutation.isPending}
            >
              إلغاء الطلب
            </Button>
          ) : undefined
        }
      >
        {detail && (
          <div className="space-y-5">
            <OrderTimeline order={detail} />

            <div>
              <h3 className="mb-2 text-sm font-semibold text-fg">الأصناف</h3>
              <ul className="divide-y divide-border rounded-lg border border-border">
                {detail.items.map((item) => (
                  <li key={item.id} className="flex items-center justify-between gap-2 p-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm text-fg">{item.nameSnapshot}</p>
                      <p className="text-xs text-fg-subtle" dir="ltr">
                        {formatNumber(item.quantity)} × {formatMoney(item.unitPrice)}
                      </p>
                    </div>
                    <span className="shrink-0 font-medium text-fg" dir="ltr">{formatMoney(item.lineTotal)}</span>
                  </li>
                ))}
              </ul>
            </div>

            {detail.addressSnapshot && (
              <div className="rounded-lg bg-bg-subtle p-3 text-sm">
                <p className="text-xs text-fg-subtle">عنوان التوصيل</p>
                <p className="mt-0.5 text-fg">{detail.addressSnapshot}</p>
              </div>
            )}

            <div className="space-y-1 border-t border-border pt-3 text-sm">
              <div className="flex justify-between text-fg-muted">
                <span>المجموع</span>
                <span dir="ltr">{formatMoney(detail.subtotal)}</span>
              </div>
              {detail.deliveryFee > 0 && (
                <div className="flex justify-between text-fg-muted">
                  <span>التوصيل</span>
                  <span dir="ltr">{formatMoney(detail.deliveryFee)}</span>
                </div>
              )}
              <div className="flex justify-between border-t border-border pt-2 text-base font-bold text-fg">
                <span>الإجمالي</span>
                <span dir="ltr">{formatMoney(detail.total)}</span>
              </div>
            </div>
          </div>
        )}
      </Dialog>
    </div>
  )
}
